import type {NfConfigService} from "@/services/NfConfigService";
import type {BookingEngineConfigModel} from "@/serviceWorker/workerComponents/Models/BookingEngineConfigModel";
import type {AdditionDescription} from "@/serviceWorker/workerComponents/Models/AdditionDescription";
import type {ChosenReservationModel} from "@/serviceWorker/workerComponents/Models/ChosenReservationModel";
import type {ShopChosenModel} from "@/serviceWorker/workerComponents/Models/ShopChosenModel";
import type {ShopChosenItem} from "@/serviceWorker/workerComponents/Models/ShopChosenItem";
declare global {
    interface Window {
        fbq: any;
        _fbq: any;
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}
export class AnalyticsService {
    declare config: BookingEngineConfigModel;

    constructor(private configService: NfConfigService, private widgetId: number) {
        this.loadConfig();
    }

    /**
     * inicjalizacja facebook pixel
     * @param trackerId
     */
    public registerPixelTracker(trackerId: string): void {
        if (!window.fbq) {
            (function (f: Window, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
            //@ts-ignore
            fbq('init', trackerId);
            //@ts-ignore
            fbq('track', 'PageView');
        }
    }

    /**
     * Google analytics 4 tracker
     * @param trackerId
     */
    public registerAnalyticsTracker(trackerId: string): void {
        if(!window.gtag) {
            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://www.googletagmanager.com/gtag/js?id='+trackerId;
            window.dataLayer = window.dataLayer || [];
            window.gtag = function gtag(){window.dataLayer.push(arguments);}
            // @ts-ignore
            gtag('js', new Date());
            //@ts-ignore
            gtag('config', trackerId);
            document.head.appendChild(script);
        }

        // Nie inicjalizujemy gtaga, bo najprawdopodobniej jest już zainicjalizowany
        // if(!window.gtagHotelTrackerRegistered){
        //     window.gtag('config', trackerId);
        //     window.gtagHotelTrackerRegistered = true;
        // }
    }
    private async loadConfig() {
        if(this.config) {
            return this.config;
        }
        const config = await this.configService.configurationPromise;
        this.config = config;
        if(this.config.google_analytics?.length) {
            this.registerAnalyticsTracker(this.config.google_analytics);
        }
        if(this.config.facebook_pixel?.length) {
            this.registerPixelTracker(this.config.facebook_pixel);
        }
        return config;
    }

//     ---- nowe funkcje ---
    private isActiveGoogleTags = () => window.gtag && this.config;

    private isActiveFacebookPixel = () => window.fbq && this.config;

    private getConfigCurrency = () => this.config.system_currency || 'PLN';

    private getRandomTransactionId = () =>  Math.floor(Math.random() * 10000000);

    //klucze do google anal
    private readonly GOOGLE_VIEW_ITEM_LIST = 'view_item_list';
    private readonly GOOGLE_VIEW_ITEM = 'view_item';
    private readonly GOOGLE_ADD_TO_CART = 'add_to_cart';
    private readonly GOOGLE_REMOVE_FROM_CART = 'remove_from_cart';
    private readonly GOOGLE_SELECT_CONTENT = 'select_content';
    private readonly GOOGLE_SHARE = 'share';
    private readonly GOOGLE_PURCHASE = 'purchase';
    private readonly GOOGLE_ADD_TO_WISHLIST = 'add_to_wishlist';

    //klucze do fb
    private readonly FACEBOOK_VIEW_CONTENT = 'ViewContent';
    private readonly FACEBOOK_ADD_TO_CART = 'AddToCart';
    private readonly FACEBOOK_PURCHASE = 'Purchase';


    public facebookViewContent = () => {
        if(!this.isActiveFacebookPixel()) return;

        window.fbq('track',this.FACEBOOK_VIEW_CONTENT, {
            item_list_id: 'view_item_list' + this.widgetId,
            item_list_name: 'Wyświetlono listę pokoi',
        })
    }

    public facebookAddToCart = (data:{
        standardId: number,
        standardName: string,
        offerName: string,
        price: number
    }) => {
        if(!this.isActiveFacebookPixel()) return;
        window.fbq('track', this.FACEBOOK_ADD_TO_CART, {
            item_id: String(data.standardId),
            item_name: data.standardName,
            currency:  this.getConfigCurrency(),
            value: data.price,
            item_variant: data.offerName
        })
    }

    public facebookPurchase = (reservations: {
        standardName: string,
        fullPrice: number,
        shop: ShopChosenModel
    }) => {
        if(!this.isActiveFacebookPixel()) return;
        const fbContents = [];
        fbContents.push(
            {
                id: reservations.standardName,
                quantity: 1
            });
        if (reservations.shop.chosenItems) {
            for (const item of Object.values(reservations.shop.chosenItems)) {
                fbContents.push({
                    id: item.name,
                    quantity: item.count
                })
            }
        }
        window.fbq('track', this.FACEBOOK_PURCHASE, {
            item_list_id: 'purchase_' + this.widgetId,
            content_type: 'product',
            contents: fbContents,
            currency: this.getConfigCurrency(),
            value: reservations.fullPrice
        })
    }


    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający wyświetli listę pokoi.
     */
    public googleViewItemList(): void {
        if (!this.isActiveGoogleTags()) return;

        window.gtag('event', this.GOOGLE_VIEW_ITEM_LIST, {
            item_list_id: 'view_item_list' + this.widgetId,
            item_list_name: 'Wyświetlono listę pokoi',
        })
    }

    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający wyświetli szczegółowy opis i zdjęcia pokoju.
     */
    public googleViewItem(standard: {roomstandard_id: number,rsl_name: string}): void {
        if(!this.isActiveGoogleTags()) return;

        window.gtag('event', this.GOOGLE_VIEW_ITEM, {
            item_list_id: 'view_item_' + this.widgetId,
            item_list_name: `Wyświetlono standard ${standard.roomstandard_id}_${standard.rsl_name}`,
            items:[
                {
                    item_id: standard.roomstandard_id,
                    item_name: standard.rsl_name,
                }
            ]
        });
    }

    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający doda produkt ze sklepu do koszyka.
     */
    public googleAddToWishlist = (product:AdditionDescription,standardData: string, count: number, totalPrice: number) => {
        if(!this.isActiveGoogleTags()) return;

        const shopItems = {
            item_id: `${product.id}_${product.a_label}`,
            item_name: product.a_label,
            currency: this.getConfigCurrency(),
            price: totalPrice / count,
            quantity: count
        };

        window.gtag('event', this.GOOGLE_ADD_TO_WISHLIST, {
            currency: this.getConfigCurrency(),
            item_list_name: `Dodanie produktu: "${product.a_label}" do pokoju: ${standardData}`,
            value: totalPrice,
            transaction_id: this.getRandomTransactionId(),
            items: [shopItems],
        });
    }

    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający doda pokój do koszyka.
     */
    public googleAddToCart(data:{
                               standardId: number,
                               standardName: string,
                               offerName: string,
                               price: number
                           }): void {
        if(!this.isActiveGoogleTags()) return;

        window.gtag('event', this.GOOGLE_ADD_TO_CART, {
            item_list_id: `add_to_cart_${data.standardId}_${data.standardName}`,
            item_list_name: 'Dodanie standardu pokoju do koszyka',
            currency: this.getConfigCurrency(),
            value: data.price,
            items: [
                {
                    item_id: String(data.standardId),
                    item_name: data.standardName,
                    currency: this.getConfigCurrency(),
                    price: data.price,
                    item_variant: data.offerName
                }
            ]
        })
    }

    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający usunie wybrany pokój z koszyka.
     */
    public googleRemoveFromCart(reservation: ChosenReservationModel): void {
        if(!this.isActiveGoogleTags()) return;

        const price = reservation.calculation.prices.r_grand_total;
        const standardId = reservation.chosenStandardDescription.roomstandard_id;
        const standardName = reservation.chosenStandardDescription.rsl_name;
        const offerName = reservation.chosenOffer.name;
        const currency = this.getConfigCurrency();

        window.gtag('event', this.GOOGLE_REMOVE_FROM_CART, {
            item_list_id: 'view_item_' + this.widgetId,
            item_list_name: `Usunięto rezerwację z kosza standard_${standardId}_${standardName}`,
            currency: currency,
            price: price,
            items: [
                {
                    item_id: String(standardId),
                    item_name: standardName,
                    currency: currency,
                    price: price,
                    item_variant: offerName
                }
            ]
        });
    }


    /**
     * Zdarzenie jest wysyłane, gdy wybierze ofertę lub pakiet z listy i wyświetli dla nich dostępne pokoje.
     */
    public googleSelectContent(targetId: number, targetLabel: string, isPacket: boolean): void {
        if (!this.isActiveGoogleTags()) return;

        const key = isPacket ? 'Pakiet' : 'Oferta';

        window.gtag('event', this.GOOGLE_SELECT_CONTENT, {
            item_list_id: 'view_item_' + this.widgetId,
            item_list_name: `Wyświetlono ${key} ${targetId}`,
            items: [
                {
                    item_id: `${targetId}`,
                    item_name: `${targetLabel}_${key}`,
                }
            ]
        });
    }

    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający kliknie na udostępnianie znalezionych propozycji.
     */
    public googleShare(shareTargetLabel: string): void {
        if(!this.isActiveGoogleTags()) return;

        window.gtag('event', this.GOOGLE_SHARE, {
            item_list_id: 'share_' + this.widgetId,
            method: shareTargetLabel,
        })
    }

    /**
     * Zdarzenie jest wysyłane, gdy odwiedzający wypełni swoje dane i naciśnie ‘Rezerwuj’.
     */
    public googlePurchase(reservation: {
        standardName: string,
        fullPrice: number,
        shop: ShopChosenModel
    }): void {
        if (!this.isActiveGoogleTags()) return;

        const transactionId = this.getRandomTransactionId();
        const currency = this.getConfigCurrency();

        const mainItem = {
            item_id: reservation.standardName,
            item_name: reservation.standardName,
            currency: currency,
            price: reservation.fullPrice,
            quantity: 1
        };

        const shopItems = Object.values(reservation.shop.chosenItems).map((item: ShopChosenItem) => {
            return ({
                item_id: String(item.id),
                item_name: item.name,
                currency: currency,
                price: item.totalPrice / item.count,
                quantity: item.count
            })
        });

        const items = [mainItem, ...shopItems];

        window.gtag('event', this.GOOGLE_PURCHASE, {
            currency: currency,
            value: reservation.fullPrice,
            transaction_id: transactionId,
            items: JSON.stringify(items),
            items_list_reservations: JSON.stringify(reservation),
        });
    }
}
